import { useCallback, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { calculateCalendarPosition } from '../lib/calendarPosition';

const isButtonOutsideViewport = (buttonRect) =>
  buttonRect.bottom <= 0 ||
  buttonRect.top >= window.innerHeight ||
  buttonRect.right <= 0 ||
  buttonRect.left >= window.innerWidth;

export const useCalendarPosition = ({ calendarRef, isOpen, onClose, position = 'left' }) => {
  const { pathname } = useLocation();

  const offsetRef = useRef({ left: 0, top: 0 });
  const hasMeasuredOffsetRef = useRef(false);

  const setCalendarFixedPosition = useCallback(
    (remeasure = false) => {
      if (pathname === '/iframe.html') return;

      const calendarContainer = calendarRef?.current?.parentNode;
      const buttonRef = calendarRef?.current?.closest('#date-input-picker');
      if (!calendarContainer || !buttonRef) return;

      const buttonRect = buttonRef.getBoundingClientRect();
      if (isButtonOutsideViewport(buttonRect)) {
        onClose?.();
        return;
      }

      // Get calendar dimensions - use defaults if not available yet
      let calendarHeight = calendarContainer.offsetHeight;
      let calendarWidth = calendarContainer.offsetWidth;

      // If dimensions are 0 or not available, try to get them from getBoundingClientRect
      if (!calendarHeight || !calendarWidth) {
        const calendarRect = calendarContainer.getBoundingClientRect();
        calendarHeight = calendarRect.height || 300;
        calendarWidth = calendarRect.width || 300;
      }

      if (remeasure) {
        // Calculate transform container offset (for modals with transform CSS).
        // Set to 0,0 and hide first to measure the actual offset.
        calendarContainer.style.cssText = `
          position: fixed;
          left: 0px;
          top: 0px;
          visibility: hidden;
        `;
        const testRect = calendarContainer.getBoundingClientRect();
        offsetRef.current = { left: testRect.left, top: testRect.top };
        hasMeasuredOffsetRef.current = true;
      }

      const { left: offsetLeft, top: offsetTop } = offsetRef.current;

      // For position: fixed, coordinates are relative to viewport
      let { left, top } = calculateCalendarPosition({
        buttonRect,
        calendarWidth,
        calendarHeight,
        availableWidth: window.innerWidth,
        availableHeight: window.innerHeight,
        documentRectLeft: 0, // 0 for fixed positioning (viewport-relative)
        position
      });

      // Adjust for transform container offset
      left = left - offsetLeft;
      top = top - offsetTop;

      calendarContainer.style.cssText = `
        position: fixed;
        left: ${left}px;
        top: ${top}px;
        opacity: 1;
        z-index: 1000;
      `;
    },
    [calendarRef, onClose, pathname, position]
  );

  useLayoutEffect(() => {
    if (!isOpen) return undefined;

    hasMeasuredOffsetRef.current = false;
    setCalendarFixedPosition(true);
    const initialTimer = setTimeout(() => setCalendarFixedPosition(true), 200);

    // Throttle scroll/resize repositioning to one update per animation frame.
    let rafId = null;
    const reposition = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        setCalendarFixedPosition(!hasMeasuredOffsetRef.current);
      });
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const container = calendarRef?.current?.parentNode;
          if (container && container.style.position !== 'fixed') {
            setCalendarFixedPosition(true);
          }
        }
      });
    });

    const container = calendarRef?.current?.parentNode;
    if (container) observer.observe(container, { attributes: true });

    window.addEventListener('resize', reposition, true);
    window.addEventListener('scroll', reposition, true);

    return () => {
      clearTimeout(initialTimer);
      if (rafId) cancelAnimationFrame(rafId);
      observer.disconnect();
      window.removeEventListener('resize', reposition, true);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [isOpen, setCalendarFixedPosition, calendarRef]);
};
